import React, { useState, useEffect } from 'react';
import { usePermissions } from '../../../hooks/usePermissions';
import {
  getGuardrailsPolicy,
  createGuardrailsPolicy,
  updateGuardrailsPolicy,
} from '../../../services/cloudopsApi';
import { Modal } from '../../../components/ui/Modal';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { Button } from '../../../components/ui/Button';

interface CreateEditPolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  policyId: string | null;
}

interface PolicyFormData {
  name: string;
  description: string;
  severity: string;
  category: string;
  resource_types: string[];
  evaluation_function: string;
  is_active: boolean;
}

const EVALUATION_FUNCTIONS = [
  'evaluate_s3_public_access',
  'evaluate_s3_encryption',
  'evaluate_ec2_public_ip',
  'evaluate_ec2_no_monitoring',
  'evaluate_rds_public_access',
  'evaluate_rds_encryption',
  'evaluate_rds_multi_az',
  'evaluate_iam_access_keys_old',
  'evaluate_security_group_open_to_world',
  'evaluate_security_group_open_ports',
  'evaluate_cloudtrail_not_enabled',
  'evaluate_cloudtrail_not_multi_region',
  'evaluate_cloudtrail_no_encryption',
  'evaluate_lambda_no_vpc',
  'evaluate_lambda_no_dead_letter_queue',
  'evaluate_missing_required_tags',
];

const RESOURCE_TYPES = [
  { value: 'ec2', label: 'EC2' },
  { value: 's3', label: 'S3' },
  { value: 'rds', label: 'RDS' },
  { value: 'lambda', label: 'Lambda' },
  { value: 'iam', label: 'IAM' },
  { value: 'security_group', label: 'Security Group' },
  { value: 'vpc', label: 'VPC' },
  { value: 'cloudtrail', label: 'CloudTrail' },
];

const CreateEditPolicyModal: React.FC<CreateEditPolicyModalProps> = ({ isOpen, onClose, onSuccess, policyId }) => {
  const { isAdmin } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<PolicyFormData>({
    name: '',
    description: '',
    severity: 'medium',
    category: 'security',
    resource_types: [],
    evaluation_function: '',
    is_active: true,
  });

  const isEditMode = !!policyId;

  useEffect(() => {
    if (isOpen && isEditMode && policyId) {
      loadPolicy();
    } else if (isOpen && !isEditMode) {
      // Reset form for new policy
      setFormData({
        name: '',
        description: '',
        severity: 'medium',
        category: 'security',
        resource_types: [],
        evaluation_function: '',
        is_active: true,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, policyId, isEditMode]);

  const loadPolicy = async () => {
    setLoading(true);
    setError(null);

    try {
      const policy = await getGuardrailsPolicy(policyId!);
      setFormData({
        name: policy.name || '',
        description: policy.description || '',
        severity: policy.severity || 'medium',
        category: policy.category || 'security',
        resource_types: policy.resource_types || [],
        evaluation_function: policy.evaluation_function || '',
        is_active: policy.is_active ?? true,
      });
    } catch (err: unknown) {
      console.error('Error loading policy:', err);
      setError(err instanceof Error ? err.message : 'Failed to load policy');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!isAdmin) {
      setError('Only administrators can create or edit policies');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (formData.name.trim() === '') {
        throw new Error('Policy name is required');
      }
      if (formData.resource_types.length === 0) {
        throw new Error('At least one resource type must be selected');
      }
      if (!formData.evaluation_function) {
        throw new Error('Evaluation function is required');
      }

      const policyData = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        severity: formData.severity,
        category: formData.category,
        resource_types: formData.resource_types,
        evaluation_function: formData.evaluation_function,
        is_active: formData.is_active,
      };

      if (isEditMode) {
        await updateGuardrailsPolicy(policyId, policyData);
      } else {
        await createGuardrailsPolicy(policyData);
      }

      onSuccess();
    } catch (err: unknown) {
      console.error('Error saving policy:', err);
      const action = isEditMode ? 'update' : 'create';
      setError(err instanceof Error ? err.message : `Failed to ${action} policy`);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const target = e.target as HTMLInputElement;
    const { name, value, type, checked } = target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleResourceTypeToggle = (resourceType: string) => {
    setFormData(prev => ({
      ...prev,
      resource_types: prev.resource_types.includes(resourceType)
        ? prev.resource_types.filter(rt => rt !== resourceType)
        : [...prev.resource_types, resourceType],
    }));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? 'Edit Guardrail Policy' : 'Create Guardrail Policy'}
      maxWidth="max-w-3xl"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <p className="text-red-800 dark:text-red-200 text-sm">{error}</p>
          </div>
        )}

        <Input
          label="Policy Name"
          name="name"
          type="text"
          value={formData.name}
          onChange={handleChange}
          placeholder="e.g., S3 Bucket Public Access Block"
          required
          disabled={loading}
        />

        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Description
          </label>
          <textarea id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Describe what this guardrail policy checks..."
            rows={3}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900 dark:bg-gray-700 dark:text-white transition-colors ${
              loading ? 'opacity-50 cursor-not-allowed' : ''
            } border-gray-300 dark:border-gray-600`}
            disabled={loading}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Select
            label="Severity"
            name="severity"
            value={formData.severity}
            onChange={handleChange}
            options={[
              { value: 'critical', label: 'Critical' },
              { value: 'high', label: 'High' },
              { value: 'medium', label: 'Medium' },
              { value: 'low', label: 'Low' },
            ]}
            required
            disabled={loading}
          />

          <Select
            label="Category"
            name="category"
            value={formData.category}
            onChange={handleChange}
            options={[
              { value: 'security', label: 'Security' },
              { value: 'cost', label: 'Cost Management' },
              { value: 'governance', label: 'Governance' },
              { value: 'reliability', label: 'Reliability' },
            ]}
            required
            disabled={loading}
          />
        </div>

        <div>
          <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Resource Types <span className="text-red-500">*</span>
          </span>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {RESOURCE_TYPES.map((rt) => (
              <label
                key={rt.value}
                className="flex items-center space-x-2 p-2 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={formData.resource_types.includes(rt.value)}
                  onChange={() => handleResourceTypeToggle(rt.value)}
                  disabled={loading}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">{rt.label}</span>
              </label>
            ))}
          </div>
        </div>

        <Select
          label="Evaluation Function"
          name="evaluation_function"
          value={formData.evaluation_function}
          onChange={handleChange}
          options={EVALUATION_FUNCTIONS.map(fn => ({
            value: fn,
            label: fn.replace('evaluate_', '').replaceAll('_', ' ').replaceAll(/\b\w/g, l => l.toUpperCase()),
          }))}
          placeholder="Select evaluation function"
          required
          disabled={loading}
        />

        <div className="flex items-center">
          <input
            type="checkbox"
            id="is_active"
            name="is_active"
            checked={formData.is_active}
            onChange={handleChange}
            disabled={loading}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label htmlFor="is_active" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
            Activate policy immediately
          </label>
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={loading}
          >
            {isEditMode ? 'Update Policy' : 'Create Policy'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateEditPolicyModal;

